import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_IMAGES, API_URL, config } from '../components/Include';
import './ViewSkills.css';
import { Alerts } from '../components/notify';


interface Skill {
  id: number;
  title: string;
  description: string;
  image: string;
  detailedDescription?: string;
  galleryImages?: string[];
}

const ViewSkills = () => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [filteredSkills, setFilteredSkills] = useState<Skill[]>([]);
  // No longer using categories
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  // No longer using categories for filtering
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [skillModal, setSkillModal] = useState<any>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [skillsPerPage] = useState<number>(12); // Increased items per page
  const [pageRange] = useState<number>(1); // Number of pages to show before and after current page


  const fetchSkills = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
     
    const url = API_URL + '/website/application/skills';

      const response = await  axios.get(url,  config)
      if (response.data.status === 200) {
       
        setSkills(response.data.data);
          setFilteredSkills(response.data.data);

      }
    } catch (error: any) {
      Alerts('danger', error.message, 'Error fetching available skills');
    } finally {
      setLoading(false);
    }
  };


  // Fetch skills from API
  useEffect(() => {
    fetchSkills();
  }, []);



  useEffect(() => {
    const filterSkills = () => {
      if (!skills.length) return;
      if (searchTerm) {
        setFilteredSkills(skills.filter((skill:any) => 
          skill.title.toLowerCase().includes(searchTerm.toLowerCase())
        ));
      } else {
        setFilteredSkills(skills);
      }
    };
    
    filterSkills();
    setCurrentPage(1);
  }, [searchTerm, skills]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Pagination handlers
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top of skills section for better UX
    const skillsSection = document.querySelector('.skills-grid') as HTMLElement;
    window.scrollTo({
      top: skillsSection?.offsetTop || 0,
      behavior: 'smooth'
    });
  };

  const goToFirstPage = () => paginate(1);
  
  const goToLastPage = () => {
    const lastPage = Math.ceil(filteredSkills.length / skillsPerPage);
    paginate(lastPage);
  };
  
  const goToPreviousPage = () => {
    if (currentPage > 1) {
      paginate(currentPage - 1);
    }
  };
  
  const goToNextPage = () => {
    const lastPage = Math.ceil(filteredSkills.length / skillsPerPage);
    if (currentPage < lastPage) {
      paginate(currentPage + 1);
    }
  };

  // Get current skills for pagination
  const indexOfLastSkill = currentPage * skillsPerPage;
  const indexOfFirstSkill = indexOfLastSkill - skillsPerPage;
  const currentSkills = filteredSkills.slice(indexOfFirstSkill, indexOfLastSkill);

  // No longer filtering by category

  const openSkillModal = (skill: Skill) => {
    setSelectedSkill(skill);
    
    // Initialize modal if not already initialized
    if (!skillModal) {
      const modalElement = document.getElementById('skillDetailModal');
      if (modalElement) {
        // Using vanilla JS to show the modal with Bootstrap
        // @ts-ignore - Bootstrap is loaded globally in the project
        const bsModal = new window.bootstrap.Modal(modalElement);
        setSkillModal(bsModal);
        bsModal.show();
      }
    } else {
      skillModal.show();
    }
  };

  return (
    <div className="view-skills-container">
      {/* Enhanced Page Header */}
      <div className="page-header skills-page-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="page-header-content">
                <h1>Explore Available Skills</h1>
                <p className="lead">
                  Discover and learn valuable skills that will enhance your career at the Entrepreneurship Development Center
                </p>
                <div className="page-header-badges">
                  <span className="badge bg-primary"><i className="ri-book-open-line me-1"></i>Quality Training</span>
                  <span className="badge bg-success"><i className="ri-user-star-line me-1"></i>Expert Instructors</span>
                  <span className="badge bg-info"><i className="ri-medal-line me-1"></i>Certification</span>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="page-header-image">
                 <div className="shape-1"></div>
                <div className="shape-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Search and Filter Section */}
      <div className="skills-search-section">
        <div className="container">
          <div className="skills-search-wrapper">
            <div className="row align-items-center">
              <div className="col-lg-4">
                <h2>Available Skills</h2>
                <p>Find the perfect skill to learn</p>
              </div>
              <div className="col-lg-8">
                <div className="skills-search-filters">
                  <div className="row g-2">
                    <div className="col-md-8">
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="ri-search-line"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search skills..."
                          value={searchTerm}
                          onChange={handleSearchChange}
                        />
                      </div>
                    </div>
                    {/* Category filter removed */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container skills-content">
        {loading ? (
          <div className="skills-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading available skills...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        ) : filteredSkills.length === 0 ? (
          <div className="no-skills-found">
            <i className="ri-file-search-line"></i>
            <h3>No skills found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <div className="row g-4">
            {filteredSkills.map((skill) => (
              <div key={skill.id} className="col-lg-4 col-md-6">
                <div className="skill-card">
                  <div className="skill-image">
                       <img  src={API_IMAGES + '/skill/' + skill.image} 
                                            onError={(e) => {
                                              (e.target as HTMLImageElement).onerror = null; 
                                              (e.target as HTMLImageElement).src = API_IMAGES + '/no.jpg';
                                            }} 
                                            alt={skill.title} />

                  </div>
                  <div className="skill-details">
                    <h3>{skill.title}</h3>
                    <p>{skill.description}</p>
                    <button 
                      className="btn btn-primary btn-sm" 
                      onClick={(e) => {
                        e.preventDefault();
                        openSkillModal(skill);
                      }}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {!loading && !error && filteredSkills.length > 0 && (
          <div className="skills-footer">
            <p>
              Showing {currentSkills.length} of {filteredSkills.length} available skills
            </p>
            <p className="skills-note">
              <i className="ri-information-line"></i>
              Click on "View Details" to learn more about each skill
            </p>
            
            {/* Simplified Pagination Controls */}
            {filteredSkills.length > skillsPerPage && (
              <div className="pagination-container">
                <div className="pagination-info">
                  Showing {indexOfFirstSkill + 1} to {Math.min(indexOfLastSkill, filteredSkills.length)} of {filteredSkills.length} skills
                </div>
                <ul className="pagination">
                  {/* First Page Button */}
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={goToFirstPage}
                      disabled={currentPage === 1}
                      aria-label="First page"
                    >
                      &laquo;
                    </button>
                  </li>
                  
                  {/* Previous Page Button */}
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      aria-label="Previous page"
                    >
                      &lsaquo;
                    </button>
                  </li>
                  
                  {/* Simple Page Numbers - Show only up to 5 pages */}
                  {(() => {
                    const totalPages = Math.ceil(filteredSkills.length / skillsPerPage);
                    const pages = [];
                    
                    // Determine which page numbers to show
                    let startPage = Math.max(1, currentPage - 1);
                    let endPage = Math.min(totalPages, startPage + 2);
                    
                    // Adjust if we're at the end
                    if (endPage === totalPages) {
                      startPage = Math.max(1, endPage - 2);
                    }
                    
                    // Generate page buttons
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => paginate(i)}>
                            {i}
                          </button>
                        </li>
                      );
                    }
                    
                    return pages;
                  })()} 
                  
                  {/* Next Page Button */}
                  <li className={`page-item ${currentPage === Math.ceil(filteredSkills.length / skillsPerPage) ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={goToNextPage}
                      disabled={currentPage === Math.ceil(filteredSkills.length / skillsPerPage)}
                      aria-label="Next page"
                    >
                      &rsaquo;
                    </button>
                  </li>
                  
                  {/* Last Page Button */}
                  <li className={`page-item ${currentPage === Math.ceil(filteredSkills.length / skillsPerPage) ? 'disabled' : ''}`}>
                    <button 
                      className="page-link" 
                      onClick={goToLastPage}
                      disabled={currentPage === Math.ceil(filteredSkills.length / skillsPerPage)}
                      aria-label="Last page"
                    >
                      &raquo;
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Skill Detail Modal */}
      <div className="modal fade skill-detail-modal" id="skillDetailModal" tabIndex={-1} aria-labelledby="skillDetailModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            {selectedSkill && (
              <>
                <div className="modal-header">
                  <h5 className="modal-title" id="skillDetailModalLabel">{selectedSkill.title}</h5>
                  <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div className="modal-body">
                  {/* Metadata section removed */}
                  
                  <div className="skill-detail-description">
                    <h5>About this Skill</h5>
                    <p>{selectedSkill.detailedDescription}</p>
                  </div>
                  
                  <h5 className="mb-3">Students in Training</h5>
                  <div className="skill-gallery">
                    <div className="row g-3">
                      {selectedSkill.galleryImages?.map((img, index) => (
                        <div key={index} className="col-md-4">

                          
                          <img 
                            src={img} 
                            alt={`${selectedSkill.title} training ${index + 1}`} 
                            className="img-fluid rounded gallery-img" 
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/skill-placeholder.jpg';
                            }}
                          />
                        </div>
                      )) || (
                        <div className="col-12">
                          <p className="text-muted">No gallery images available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewSkills;
